import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import SeoHead from '@/components/feature/SeoHead';
import { supabase } from '@/lib/supabase';

const FORM_URL = 'https://readdy.ai/api/form/d96gel70d76aer3t54ag';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';

const CONTATTI_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contatti Discovering Marche — Ines Felicity Di Nallo',
    description: 'Contatta Discovering Marche per una consulenza gratuita sui borghi storici delle Marche. Assistenza in italiano, inglese e spagnolo.',
    url: `${SITE_URL}/contatti`,
    mainEntity: {
      '@type': 'LocalBusiness',
      name: 'Discovering Marche — Ines Felicity Di Nallo',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Castelfidardo',
        addressRegion: 'AN',
        addressCountry: 'IT',
      },
      knowsLanguage: ['it', 'en', 'es'],
      sameAs: ['https://www.instagram.com/felicity.dinallo_/'],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Contatti', item: `${SITE_URL}/contatti` },
    ],
  },
];

const ContattiPage = () => {
  const { t } = useTranslation();
  const [formSent, setFormSent] = useState(false);
  const [formError, setFormError] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [formSending, setFormSending] = useState(false);
  const header = useScrollAnimation();
  const formRef = useScrollAnimation({ threshold: 0.05 });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSending(true);
    setFormError(false);
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormSent(true);
      form.reset();
      setCharCount(0);
      setFormSending(false);
      return;
    }
    formData.delete('phone_alt');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* non-JSON response */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormSent(true);
        form.reset();
        setCharCount(0);

        // Invia notifica email via Edge Function (fire-and-forget)
        supabase.functions.invoke('send-contact-notification', {
          body: {
            nome: formData.get('nome'),
            email: formData.get('email'),
            telefono: formData.get('telefono'),
            borgo_interesse: formData.get('borgo_interesse'),
            messaggio: formData.get('messaggio'),
          },
        }).catch(() => {});
      } else {
        setFormError(true);
      }
    } catch {
      setFormError(true);
    } finally {
      setFormSending(false);
    }
  };

  return (
    <main className="min-h-screen bg-white">
      <SeoHead
        title="Contatti — Discovering Marche | Ines Felicity Di Nallo, Castelfidardo"
        description="Contatta Ines Felicity Di Nallo di Discovering Marche per una consulenza gratuita sui borghi storici delle Marche. Assistenza in italiano, inglese e spagnolo. Castelfidardo, Ancona."
        keywords="contatti Discovering Marche, consulenza borghi Marche, Ines Felicity Di Nallo, Castelfidardo Marche, agente immobiliare Marche"
        canonical="/contatti"
        jsonLd={CONTATTI_JSONLD}
      />
      {/* Hero */}
      <section className="relative h-[45vh] min-h-[340px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Marche%20Italy%20beautiful%20landscape%20rolling%20hills%20terracotta%20farmhouse%20olive%20trees%20golden%20afternoon%20light%20peaceful%20Italian%20countryside%20Ancona&width=1920&height=700&seq=contattiHero&orientation=landscape"
            alt="Contatti Marche Casa — Castelfidardo real estate"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-14">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-200 mb-4">{t('contatti.overline')}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-white font-light max-w-2xl leading-tight">
            {t('contatti.page.title')}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: info */}
          <div
            ref={header.ref}
            className={`fade-in-up ${header.isVisible ? 'visible' : ''} order-2 lg:order-1`}
          >
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">
              {t('contatti.info.overline')}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-6 leading-snug">
              {t('contatti.info.title')}
            </h2>
            <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-10">
              {t('contatti.info.text')}
            </p>

            {/* Contact details */}
            <div className="space-y-5 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-terracotta-50 rounded-full flex-shrink-0">
                  <i className="ri-map-pin-line text-terracotta-300"></i>
                </div>
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-1">{t('contatti.sede.label')}</p>
                  <p className="font-sans text-sm text-anthracite-700">Castelfidardo (AN), Italia</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-terracotta-50 rounded-full flex-shrink-0">
                  <i className="ri-instagram-line text-terracotta-300"></i>
                </div>
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-1">Instagram</p>
                  <a
                    href="https://www.instagram.com/felicity.dinallo_/"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="font-sans text-sm text-terracotta-300 hover:text-terracotta-400 transition-colors"
                  >
                    @felicity.dinallo_
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-terracotta-50 rounded-full flex-shrink-0">
                  <i className="ri-translate-2 text-terracotta-300"></i>
                </div>
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-1">{t('contatti.lingue.label')}</p>
                  <p className="font-sans text-sm text-anthracite-700">🇮🇹 Italiano · 🇬🇧 English · 🇪🇸 Español</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-terracotta-50 rounded-full flex-shrink-0">
                  <i className="ri-award-line text-terracotta-300"></i>
                </div>
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-1">{t('contatti.abilitazione.label')}</p>
                  <p className="font-sans text-sm text-anthracite-700">CCIAA Ancona — Ines Felicity Di Nallo</p>
                </div>
              </div>
            </div>

            {/* Photo */}
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Castelfidardo%20Marche%20Italy%20beautiful%20medieval%20village%20stone%20buildings%20terracotta%20roofs%20panoramic%20view%20hills%20warm%20golden%20light%20Italian%20borgo&width=700&height=520&seq=contattiPhoto&orientation=landscape"
                alt="Castelfidardo — Marche Casa"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right: form */}
          <div
            ref={formRef.ref}
            className={`fade-in-up ${formRef.isVisible ? 'visible' : ''} stagger-2 order-1 lg:order-2`}
          >
            <div className="bg-ivory-50 rounded-2xl border border-ivory-200 p-8 md:p-10">
              <h2 className="font-serif text-2xl text-anthracite-800 mb-2">{t('contatti.form.title')}</h2>
              <p className="font-sans text-sm text-anthracite-400 mb-8">{t('contatti.form.subtitle')}</p>

              {formSent ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-terracotta-50 rounded-full mx-auto mb-5">
                    <i className="ri-check-line text-3xl text-terracotta-300"></i>
                  </div>
                  <h3 className="font-serif text-xl text-anthracite-700 mb-2">{t('contatti.success.title')}</h3>
                  <p className="font-sans text-sm text-anthracite-500">{t('contatti.success')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form="">
                  {/* Honeypot anti-spam */}
                  <input type="text" name="phone_alt" className="form-phone-alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block font-sans text-xs text-anthracite-500 mb-1.5">{t('contatti.name')} *</label>
                      <input
                        type="text"
                        name="nome"
                        required
                        className="w-full px-4 py-3 bg-white border border-ivory-300 rounded-lg font-sans text-sm text-anthracite-700 focus:outline-none focus:border-terracotta-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-xs text-anthracite-500 mb-1.5">{t('contatti.email')} *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 bg-white border border-ivory-300 rounded-lg font-sans text-sm text-anthracite-700 focus:outline-none focus:border-terracotta-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block font-sans text-xs text-anthracite-500 mb-1.5">{t('contatti.phone')} *</label>
                      <input
                        type="tel"
                        name="telefono"
                        required
                        className="w-full px-4 py-3 bg-white border border-ivory-300 rounded-lg font-sans text-sm text-anthracite-700 focus:outline-none focus:border-terracotta-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-xs text-anthracite-500 mb-1.5">{t('contatti.borgo.label')} *</label>
                      <input
                        type="text"
                        name="borgo_interesse"
                        required
                        className="w-full px-4 py-3 bg-white border border-ivory-300 rounded-lg font-sans text-sm text-anthracite-700 focus:outline-none focus:border-terracotta-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-sans text-xs text-anthracite-500 mb-1.5">{t('contatti.message')} *</label>
                    <textarea
                      name="messaggio"
                      rows={5}
                      required
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 bg-white border border-ivory-300 rounded-lg font-sans text-sm text-anthracite-700 focus:outline-none focus:border-terracotta-300 transition-colors resize-none"
                    />
                    <p className="font-sans text-xs text-anthracite-400 text-right mt-1">{charCount}/500</p>
                  </div>
                  {formError && (
                    <p className="font-sans text-xs text-red-500">{t('contatti.error')}</p>
                  )}
                  <button
                    type="submit"
                    disabled={formSending}
                    className="w-full py-4 bg-terracotta-300 text-white font-sans text-sm tracking-widest uppercase rounded-full hover:bg-terracotta-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {formSending ? 'Invio...' : t('contatti.submit')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContattiPage;